'use client';

import React from 'react';
import { PaginationProps } from './types';
import ChevronDown from '../Icons/ChevronDown';

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className = '',
}) => {
  if (totalPages <= 1) return null;

  // Button base styles
  const baseButtonStyles =
    'bg-pagination-count-bg text-pagination-count-text flex items-center justify-center text-base font-semibold border-none cursor-pointer transition-all duration-200 p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary-500';
  const activePageStyles =
    'w-6.5 h-6.5 bg-pagination-count-bg text-pagination-count-text rounded-full relative -left-0.75';
  const inactivePageStyles = 'min-w-5 bg-transparent text-primary-500 hover:text-secondary-500';

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage <= 3) {
        for (let i = 2; i <= Math.min(maxVisible, totalPages - 1); i++) {
          pages.push(i);
        }
        pages.push('...');
      } else if (currentPage >= totalPages - 2) {
        pages.push('...');
        for (let i = Math.max(2, totalPages - maxVisible + 1); i < totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <nav
      className={`flex items-center justify-center gap-5 w-full max-w-81 mx-auto ${className}`}
      aria-label='Pagination'
    >
      {/* Previous Button */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className='flex items-center justify-center gap-1 text-base font-semibold text-gray-500 bg-transparent border-none cursor-pointer transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 hover:text-primary-500'
        aria-label='Previous page'
      >
        <div
          className='w-3.5 h-3.5 flex items-center justify-center rotate-90'
          aria-hidden='true'
        >
          <ChevronDown className='w-4 h-4' />
        </div>
        <span>Prev</span>
      </button>

      {/* Page Numbers */}
      <div className='flex items-center gap-1.5 md:gap-3'>
        {pageNumbers.map((pageNum, idx) => {
          if (pageNum === '...') {
            return (
              <span
                key={`ellipsis-${idx}`}
                className='text-primary-500 cursor-default'
                aria-hidden='true'
              >
                ...
              </span>
            );
          }

          const isActive = pageNum === currentPage;
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum as number)}
              className={`${baseButtonStyles} ${isActive ? activePageStyles : inactivePageStyles}`}
              aria-label={`Page ${pageNum}`}
              aria-current={isActive ? 'page' : undefined}
            >
              {pageNum}
            </button>
          );
        })}
      </div>

      {/* Next Button */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className='flex items-center justify-center gap-1 text-base font-semibold text-primary-500 bg-transparent border-none cursor-pointer transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 hover:text-secondary-500'
        aria-label='Next page'
      >
        <span>Next</span>
        <div
          className='w-3.5 h-3.5 flex items-center justify-center -rotate-90'
          aria-hidden='true'
        >
          <ChevronDown className='w-4 h-4' />
        </div>
      </button>
    </nav>
  );
};

export default Pagination;
